/**
 * Client for a workspace's shared CNC machine profiles
 * (`server/modules/integrations/cnc/`), mounted at
 * `/workspaces/:id/cnc-profiles`.
 *
 * Any member may read the list; the owner and Admins/Managers may replace it
 * (the response's `canEdit` says which this user is). The server checks every
 * profile with the same `validateCncProfile` the Ring Builder's dialog uses
 * (`src/ui/ring/cnc/cncProfile.ts`), so run it before saving and the save
 * will not be refused for a profile's own fields.
 *
 * Saving REPLACES the whole list: send every profile to keep. Pass back the
 * `updatedAt` you loaded as `expectedUpdatedAt`, and a list someone else
 * changed in the meantime is refused (an Error whose message says so) instead
 * of being overwritten.
 */
import type { CncMachineProfile } from "../../shared/api-contract";
import { apiGet, apiPut } from "../shared/apiClient";

/** Profiles one workspace may keep (`MAX_CNC_PROFILES` on the server). */
export const MAX_WORKSPACE_CNC_PROFILES = 20;

export interface WorkspaceCncProfiles {
  profiles: CncMachineProfile[];
  /** The `name` of the profile the CNC dialog opens on; null for none. */
  defaultProfileId: string | null;
  /** ISO time of the last save; null when the workspace has never saved a list. */
  updatedAt: string | null;
  /** User id of whoever saved it last. */
  updatedBy: string | null;
  /** Whether this user may save the list. */
  canEdit: boolean;
  /**
   * Stored profiles that fail TODAY's `validateCncProfile` (a rule tightened
   * after they were saved). Still listed so they can be fixed; never send one
   * to the viewer as it is.
   */
  invalid: { name: string; errors: string[] }[];
}

export interface SaveWorkspaceCncProfiles {
  /** At most 20, names unique: a profile is chosen by its name. */
  profiles: CncMachineProfile[];
  /** The name of one of `profiles`; omit or null for none. */
  defaultProfileId?: string | null;
  /** The `updatedAt` this edit started from; null when it started from no list. Omit to overwrite unconditionally. */
  expectedUpdatedAt?: string | null;
}

const path = (workspaceId: string) => `/workspaces/${encodeURIComponent(workspaceId)}/cnc-profiles`;

export const getWorkspaceCncProfiles = (workspaceId: string) => apiGet<WorkspaceCncProfiles>(path(workspaceId));

export const saveWorkspaceCncProfiles = (workspaceId: string, body: SaveWorkspaceCncProfiles) =>
  apiPut<WorkspaceCncProfiles>(path(workspaceId), body);
